import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import TechniqueCards from "@/components/TechniqueCards";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Check } from "@/components/Icon";
import werkkledingAsset from "@/assets/hoodie-aanbieding.webp.asset.json";
import imgPolos from "@/assets/categories/card-polos.webp";
import imgSweaters from "@/assets/categories/card-sweaters-en-hoodies.webp";
import imgWerk from "@/assets/categories/card-werkkleding.webp";
import imgTshirts from "@/assets/categories/card-t-shirts.webp";
import teamAsset from "@/assets/een-professionele-uitstraling-voor-je-hele-team.webp.asset.json";

const HERO_IMAGE = "/hero-magicprint.webp";
const H1 = "Kleding bedrukken in Rotterdam";

const h2Cls = "text-3xl font-bold text-foreground md:text-4xl";
const bodyCls = "mt-5 space-y-4 text-lg leading-relaxed text-muted-foreground";

const products = [
  { title: "Polo's bedrukken", text: "Een polo met bedrijfslogo zorgt voor een verzorgde en professionele uitstraling. Geschikt voor medewerkers in winkels, horeca en andere bedrijven.", image: imgPolos, to: "/producten?cat=polo" },
  { title: "Hoodies en sweaters bedrukken", text: "Comfortabele kleding voor medewerkers, verenigingen en evenementen. Verkrijgbaar in verschillende kleuren en uitvoeringen.", image: imgSweaters, to: "/producten?cat=sweater" },
  { title: "Werkkleding bedrukken", text: "Laat werkkleding voorzien van je bedrijfslogo voor een herkenbare uitstraling tijdens het werk.", image: imgWerk, to: "/bedrijfskleding-werkkleding" },
  { title: "T-shirts bedrukken", text: "Bedrukte T-shirts zijn geschikt voor promotieacties, evenementen en dagelijks gebruik binnen je bedrijf.", image: imgTshirts, to: "/t-shirt-bedrukken-in-rotterdam" },
];

const faqs = [
  { q: "Vanaf hoeveel stuks kan ik kleding laten bedrukken?", a: "Bij Magic Print kun je kleding laten bedrukken vanaf 10 stuks. De mogelijkheden zijn afhankelijk van het kledingstuk en de gewenste bedrukking." },
  { q: "Kan ik verschillende maten bestellen?", a: "Ja, binnen een bestelling kun je doorgaans verschillende kledingmaten combineren. Geef bij je aanvraag aan welke maten en aantallen je nodig hebt." },
  { q: "Kan ik mijn eigen logo laten bedrukken?", a: "Ja, je kunt kleding laten bedrukken met je eigen bedrijfslogo, ontwerp of tekst. Bij voorkeur ontvangen we een vectorbestand, zodat we de kwaliteit van de bedrukking goed kunnen beoordelen." },
  { q: "Welke kleding is geschikt om te bedrukken?", a: "Onder andere T-shirts, polo's, hoodies, sweaters en verschillende soorten werkkleding. Welke bedrukking mogelijk is, hangt af van het materiaal en het ontwerp." },
  { q: "Hoe lang duurt het voordat mijn kleding klaar is?", a: "De levertijd hangt af van het kledingstuk, de oplage en de gekozen druktechniek. Bij je offerte kunnen we aangeven wat de verwachte levertijd is." },
  { q: "Hebben jullie een drukkerij in Rotterdam?", a: "Nee, onze eigen drukkerij bevindt zich in Den Haag. We verzorgen wel kledingbedrukking voor bedrijven, verenigingen en organisaties in Rotterdam en omgeving." },
];

const Hero = () => (
  <section className="relative min-h-[680px] overflow-hidden pt-28 md:min-h-[720px] md:pt-36">
    <img
      src={HERO_IMAGE}
      alt={H1}
      className="absolute inset-0 h-full w-full object-cover"
      width={1600}
      height={627}
      fetchPriority="high"
    />
    <div className="absolute inset-0 bg-surface-dark/70" />
    <div className="container relative z-10 flex min-h-[550px] items-center py-12 md:min-h-[584px] md:py-16">
      <div className="max-w-3xl text-surface-dark-foreground">
        <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">{H1}</h1>
        <div className="mt-6 max-w-2xl space-y-3 text-base leading-relaxed text-surface-dark-foreground/90 md:text-lg">
          <p>Wil je kleding laten bedrukken voor jouw bedrijf, vereniging of evenement in Rotterdam? Bij Magic Print kun je terecht voor verschillende soorten kleding met een eigen logo, ontwerp of tekst.</p>
          <p>Van representatieve polo's en comfortabele hoodies tot bedrukte werkkleding: wij verzorgen het bedrukken in onze eigen drukkerij in Den Haag.</p>
        </div>
        <ul className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-7">
          {["Kleding bedrukken vanaf 10 stuks", "Eigen productie en persoonlijk advies", "Verschillende kledingstukken en druktechnieken"].map((usp) => (
            <li key={usp} className="flex items-center gap-2 font-semibold">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary">
                <Check className="h-3 w-3" />
              </span>
              {usp}
            </li>
          ))}
        </ul>
        <Button size="lg" className="mt-8" asChild>
          <Link to="/offerte">
            Vraag vrijblijvend een offerte aan <ArrowRight />
          </Link>
        </Button>
      </div>
    </div>
  </section>
);

// Full-bleed band inside PageLayout's centred container (same as the T-shirt Rotterdam page).
const bleed = "mx-[calc(50%-50vw)]";
const gridCls = "grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16";

const KledingBedrukkenRotterdam = () => (
  <PageLayout
    title={H1}
    seoTitle="Kleding bedrukken Rotterdam | Vanaf 10 stuks – Magic Print"
    metaDescription="Kleding laten bedrukken in Rotterdam? Van polo's en hoodies tot bedrijfskleding. Eigen drukkerij, vanaf 10 stuks en persoonlijk advies."
    ogImage={HERO_IMAGE}
    hideDefaultCta
    hero={<Hero />}
  >
    <section className="-mt-16 py-14 md:-mt-20 md:py-16">
      <div className={gridCls}>
        <div>
          <h2 className={h2Cls}>Kleding laten bedrukken in Rotterdam</h2>
          <div className={bodyCls}>
            <p>Bedrukte kleding zorgt voor een herkenbare uitstraling. Of je nu medewerkers wilt voorzien van bedrijfskleding, kleding zoekt voor een vereniging of een evenement organiseert: met een eigen bedrukking maak je de kleding persoonlijk.</p>
            <p>Bij Magic Print helpen we klanten uit Rotterdam en omgeving met het kiezen van geschikte kleding en een passende druktechniek.</p>
            <p>We kijken daarbij naar het materiaal, het ontwerp en het aantal kledingstukken. Zo krijg je advies dat aansluit op jouw wensen en het gebruik van de kleding.</p>
          </div>
          <Button size="lg" variant="outline" className="mt-8" asChild>
            <Link to="/producten">Bekijk onze kleding <ArrowRight /></Link>
          </Button>
        </div>
        <img
          src={werkkledingAsset.url}
          alt="Bedrukte hoodie met logo"
          className="aspect-[4/3] w-full rounded-2xl object-cover"
          width={800}
          height={600}
          loading="lazy"
          decoding="async"
        />
      </div>
    </section>

    <section className="border-t border-border py-16 md:py-24">
      <div className="mx-auto mb-10 max-w-3xl text-center">
        <h2 className={h2Cls}>Welke kleding kun je laten bedrukken?</h2>
        <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
          Niet ieder kledingstuk is geschikt voor hetzelfde doel. Daarom kun je bij Magic Print kiezen uit verschillende soorten kleding.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {products.map((p) => (
          <Link
            key={p.title}
            to={p.to}
            className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:border-primary hover:shadow-xl"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={p.image}
                alt={p.title}
                loading="lazy"
                width={600}
                height={450}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-1 flex-col p-6">
              <h3 className="mb-2 text-xl font-bold text-foreground transition-colors group-hover:text-primary">{p.title}</h3>
              <p className="mb-4 flex-1 leading-relaxed text-muted-foreground">{p.text}</p>
              <span className="flex items-center gap-1 text-sm text-primary">
                Bekijk meer <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>

    <section className={`${bleed} bg-secondary py-16 md:py-24`}>
      <div className="container">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <h2 className={h2Cls}>Welke druktechniek past bij jouw kleding?</h2>
          <div className="mt-4 space-y-3 text-lg leading-relaxed text-muted-foreground">
            <p>De juiste druktechniek hangt af van het kledingstuk, het ontwerp en de gewenste oplage.</p>
            <p>Bij Magic Print werken we met verschillende technieken om kleding van een passende bedrukking te voorzien.</p>
          </div>
        </div>
        <TechniqueCards
          items={["zeefdruk", "transferdruk", "dtf", "borduren"]}
          descriptions={{
            zeefdruk: "Geschikt voor grotere oplages en ontwerpen met een beperkt aantal kleuren.",
            transferdruk: "Een veelzijdige techniek voor verschillende soorten kleding en toepassingen.",
            dtf: "Geschikt voor gedetailleerde ontwerpen en afbeeldingen met meerdere kleuren.",
            borduren: "Een duurzame en representatieve afwerking, bijvoorbeeld voor polo's, sweaters en bedrijfskleding.",
          }}
        />
        <div className="mt-10 flex justify-center">
          <Button size="lg" variant="outline" asChild>
            <Link to="/druktechnieken">Bekijk onze druktechnieken <ArrowRight /></Link>
          </Button>
        </div>
      </div>
    </section>

    <section className={`${bleed} bg-surface-dark py-16 text-surface-dark-foreground md:py-24`}>
      <div className="container max-w-4xl text-center">
        <h2 className="text-3xl font-bold md:text-4xl">Kleding bedrukken vanaf 10 stuks</h2>
        <div className="mt-6 space-y-4 text-lg leading-relaxed text-surface-dark-foreground/80">
          <p>Of je nu tien polo's nodig hebt voor je medewerkers of een grotere oplage hoodies voor een evenement: bij Magic Print kun je kleding laten bedrukken vanaf 10 stuks.</p>
          <p>De prijs is afhankelijk van het kledingstuk, het aantal, het ontwerp en de gekozen druktechniek.</p>
          <p>Daarom maken we graag een offerte op maat. Zo weet je vooraf wat de mogelijkheden en kosten zijn.</p>
        </div>
        <div className="mt-8 flex justify-center">
          <Button size="lg" asChild>
            <Link to="/offerte">Vraag een offerte aan <ArrowRight /></Link>
          </Button>
        </div>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className={gridCls}>
        <img
          src={teamAsset.url}
          alt="Bedrukte bedrijfskleding voor een team"
          className="aspect-[4/3] w-full rounded-2xl object-cover lg:order-1"
          width={800}
          height={600}
          loading="lazy"
          decoding="async"
        />
        <div className="lg:order-2">
          <h2 className={h2Cls}>Bedrukte kleding voor bedrijven en verenigingen in Rotterdam</h2>
          <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
            <p>Rotterdam kent veel verschillende bedrijven, horecazaken en verenigingen. Voor iedere organisatie kan bedrukte kleding een andere functie hebben.</p>
            <p>Een horecabedrijf wil bijvoorbeeld dat medewerkers herkenbaar zijn, terwijl een bouwbedrijf vooral praktische werkkleding nodig heeft. Bij een vereniging of evenement draait het juist vaak om een gezamenlijke uitstraling.</p>
            <p>Wij helpen je bij het kiezen van kleding die past bij jouw organisatie en adviseren over de mogelijkheden voor de bedrukking.</p>
            <p>Onze drukkerij bevindt zich in Den Haag. Vanuit daar verzorgen we opdrachten voor klanten in Rotterdam en de omliggende regio.</p>
          </div>
          <Button size="lg" variant="outline" className="mt-8" asChild>
            <Link to="/contact">Neem contact op <ArrowRight /></Link>
          </Button>
        </div>
      </div>
    </section>

    <section className="border-t border-border py-16 md:py-24">
      <div className="mx-auto max-w-4xl text-center">
        <h2 className={h2Cls}>Kleding laten bedrukken in Rotterdam?</h2>
        <div className="mx-auto mt-5 max-w-3xl space-y-3 text-lg leading-relaxed text-muted-foreground">
          <p>Wil je polo's, hoodies, werkkleding of andere kleding laten bedrukken met je eigen logo?</p>
          <p>Vertel ons wat je zoekt en hoeveel kledingstukken je nodig hebt. We denken graag met je mee over de mogelijkheden.</p>
        </div>
        <div className="mt-8 flex justify-center">
          <Button size="lg" asChild>
            <Link to="/offerte">Vraag vrijblijvend een offerte aan <ArrowRight /></Link>
          </Button>
        </div>
      </div>
    </section>

    {/* Extra bottom space so the floating quick-contact bar never covers the FAQ. */}
    <section className={`${bleed} -mb-20 bg-secondary pb-32 pt-16 md:pb-28 md:pt-24`} id="veelgestelde-vragen">
      <div className="container max-w-3xl">
        <h2 className={`${h2Cls} mb-10 text-center`}>Veelgestelde vragen over kleding bedrukken in Rotterdam</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`faq-${i}`}
              className="rounded-lg border border-border bg-card px-6 data-[state=open]:border-primary/40 [&_[role=region][data-state=closed]]:hidden"
            >
              <AccordionTrigger className="text-left font-medium hover:no-underline">
                <h3 className="text-base font-medium text-foreground">{f.q}</h3>
              </AccordionTrigger>
              <AccordionContent forceMount className="data-[state=closed]:hidden leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  </PageLayout>
);

export default KledingBedrukkenRotterdam;
